import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../Layout/Layout';
import { AuthContext } from '../../contexts/AuthContext';
import { paymentMethodService } from '../../services/paymentMethodService';
import Dialog from '../common/Dialog';
import UpgradeBar from '../common/UpgradeBar';

const Container = styled.div`
  padding: 24px;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: var(--surface);
  padding: 24px;
  border-radius: 16px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border);
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 36px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
`;

const NewPaymentButton = styled.button`
  background: var(--gradient-primary);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-sm);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SearchContainer = styled.div`
  background: var(--surface);
  padding: 32px;
  border-radius: 20px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border);
  animation: slideUp 0.5s ease-out;

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  max-width: 400px;
  margin-bottom: 32px;
`;

const SearchInput = styled.input`
  padding: 14px 14px 14px 45px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  color: var(--text-primary);
  font-size: 16px;
  width: 100%;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-sm);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--shadow-md);
  }

  &:hover {
    border-color: var(--primary-light);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  font-size: 18px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  text-align: center;
  gap: 24px;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
  margin-bottom: 16px;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

const EmptyText = styled.div`
  color: var(--text-secondary);
  font-size: 18px;
  max-width: 400px;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const TableContainer = styled.div`
  background: var(--surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  overflow: hidden;
  margin-top: 24px;
  box-shadow: var(--shadow-sm);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 80px;
  padding: 16px 24px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  font-weight: 500;
  color: var(--text-secondary);
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 80px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border);
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--hover);
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--error);
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  
  &:hover {
    background: var(--error-light);
  }
`;

const MethodCount = styled.span`
  font-size: 16px;
  background: var(--primary-light);
  color: var(--primary);
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
`;

const PaymentMethods = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [methodCount, setMethodCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);

  const refreshPaymentMethods = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      
      // Get methods and count separately to ensure accuracy
      const methods = await paymentMethodService.getAllPaymentMethods(user.id);
      const count = await paymentMethodService.getPaymentMethodCount(user.id);
      
      setPaymentMethods(methods);
      setMethodCount(count);
      
      console.log('Current count:', count); // Add this for debugging
    } catch (error) {
      console.error('Error loading payment methods:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshPaymentMethods();
  }, [user, location.state?.refresh]);

  const handleNewPaymentMethod = () => {
    navigate('/payment-methods/create');
  };

  const handleDelete = async (method) => {
    setMethodToDelete(method);
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await paymentMethodService.deletePaymentMethod(methodToDelete.id);
      await refreshPaymentMethods();
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error deleting payment method:', error);
      alert('Failed to delete payment method');
    }
  };

  const filteredMethods = paymentMethods.filter(method =>
    method.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    method.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <Container>
        <UpgradeBar />

        <Header>
          <TitleContainer>
            <Title>Payment Methods</Title>
            {methodCount > 0 && (
              <MethodCount>{methodCount}</MethodCount>
            )}
          </TitleContainer>
          <NewPaymentButton onClick={handleNewPaymentMethod}>
            New Payment Method
          </NewPaymentButton>
        </Header>

        <SearchContainer>
          <SearchWrapper>
            <SearchInput
              type="text"
              placeholder="Search payment methods..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchIcon>🔍</SearchIcon>
          </SearchWrapper>
        </SearchContainer>

        {isLoading ? (
          <div>Loading...</div>
        ) : filteredMethods.length > 0 ? (
          <TableContainer>
            <TableHeader>
              <div>Name</div>
              <div>Type</div>
              <div>Details</div>
              <div>Actions</div>
            </TableHeader>
            {filteredMethods.map(method => (
              <TableRow key={method.id}>
                <div>{method.name}</div>
                <div>{method.type}</div>
                <div>{method.details}</div>
                <ActionButton onClick={() => handleDelete(method)}>
                  🗑️
                </ActionButton>
              </TableRow>
            ))}
          </TableContainer>
        ) : (
          <EmptyState>
            <EmptyIcon>💳</EmptyIcon>
            <EmptyText>No payment methods yet</EmptyText>
            <EmptyText>Add your first payment method to get started</EmptyText>
            <NewPaymentButton onClick={handleNewPaymentMethod}>
              Add Payment Method
            </NewPaymentButton>
          </EmptyState>
        )}
      </Container>

      {/* Confirm Delete Dialog */}
      <Dialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="Delete Payment Method"
        message={`Are you sure you want to delete "${methodToDelete?.name}"? This action cannot be undone.`}
        icon="🗑️"
        variant="danger"
        buttons={[
          {
            text: 'Delete',
            onClick: handleConfirmDelete,
            variant: 'danger'
          },
          {
            text: 'Cancel',
            onClick: () => setShowConfirmDialog(false),
            variant: 'secondary'
          }
        ]}
      />

      {/* Success Dialog */}
      <Dialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Success!"
        message="Payment method deleted successfully!"
        icon="✅"
      />
    </Layout>
  );
};

export default PaymentMethods; 